// TriggerApprovalDialog — UI för I-005 approval-flow.
//
// När AI:n försöker kalla \`trigger_workflow_run\` via tool-use returnerar
// processMotorTools-dispatchern \`approval-required\`. Denna dialog
// visar tool-call-detaljer och låter user Godkänn/Neka.
//
// Props tar ett preparat ToolCall-objekt + callback som kör dispatchern
// med \`{ approved: true }\`. Default-UX: dialog är modalt så user inte
// kan missa det.

import { useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Rocket, Shield, AlertTriangle, Sparkles } from 'lucide-react';
import type { ToolCall, ToolResult } from '@/lib/processMotorTools';

export interface TriggerApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  call: ToolCall | null;
  /** Workflow-namn från lookup av args.workflow_id — caller gör namnet. */
  workflowName?: string;
  /** Callback som utförs efter user:s Approve. Caller:n wrappar
   *  \`dispatchProcessMotorTool(call, { approved: true })\` här. */
  onApprove: (call: ToolCall) => Promise<ToolResult>;
  /** Valfri callback vid Deny — t.ex. skicka signal till AI att user sa nej. */
  onDeny?: (call: ToolCall) => void;
}

function formatArgs(args: Record<string, unknown>): string {
  try {
    return JSON.stringify(args, null, 2);
  } catch {
    return String(args);
  }
}

export function TriggerApprovalDialog({
  open, onOpenChange, call, workflowName, onApprove, onDeny,
}: TriggerApprovalDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  const handleApprove = async () => {
    if (!call) return;
    setSubmitting(true);
    try {
      await onApprove(call);
    } finally {
      setSubmitting(false);
      onOpenChange(false);
    }
  };

  const handleDeny = () => {
    if (call && onDeny) onDeny(call);
    onOpenChange(false);
  };

  if (!call) return null;

  const isWriteCall = call.name === 'trigger_workflow_run';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md" data-testid="trigger-approval-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            AI vill köra en process
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-2">
              <p className="text-sm">
                {isWriteCall ? (
                  <>
                    AI-chatten har föreslagit att köra workflow:{' '}
                    <strong>{workflowName ?? (call.args.workflow_id as string)?.slice(0, 8) ?? 'unknown'}</strong>.
                    {call.args.trigger_kind === 'test' && ' (test-mode)'}
                  </>
                ) : (
                  <>AI-chatten vill köra: <code className="font-mono">{call.name}</code></>
                )}
              </p>

              <div className="flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-2 rounded-md">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Enterprise-skydd:</strong> Writes kräver ditt
                  explicita godkännande. Server-side-triggern (P-001)
                  gör ytterligare tier-check så non-Enterprise-konton
                  blockeras oavsett.
                </div>
              </div>

              {call.args.trigger_data !== undefined && (
                <div className="text-xs">
                  <div className="font-medium mb-1">Trigger-data AI skickar:</div>
                  <pre
                    className="bg-muted p-2 rounded-md overflow-auto text-[10px] max-h-40 font-mono"
                    data-testid="trigger-approval-args"
                  >
                    {formatArgs(call.args.trigger_data as Record<string, unknown>)}
                  </pre>
                </div>
              )}

              <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
                <Shield className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <div>
                  Godkänn bara om du förstår vad processen gör. Du kan
                  se run-historiken i Process-motor-dialog:en efteråt.
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={handleDeny}
            data-testid="trigger-approval-deny"
          >
            Neka
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => void handleApprove()}
            disabled={submitting}
            data-testid="trigger-approval-approve"
          >
            <Rocket className="w-4 h-4 mr-1" />
            {submitting ? 'Kör…' : 'Godkänn & kör'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
